import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import _get from 'lodash/get'

import { CONFIGS } from '../../config/setConfigs'
import { isBrowser } from '../../utils/isBrowser'
import { getCookieByName, setCustomCookie } from '../../utils/cookies'
import { storage } from '../../utils/storage'
import { publicRequest } from './publicRequest'

publicRequest.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const guestToken = storage.getItem('auth_guest_token')
  if (guestToken) {
    config.headers['Authorization-Guest'] = guestToken
  }

  const xtfCookie = getCookieByName('X-TF-ECOMMERCE')
  if (xtfCookie) {
    config.headers['X-TF-ECOMMERCE'] = xtfCookie
  }

  const additionalCookiesHeaderValue = isBrowser && typeof document !== 'undefined' ? (document.cookie ?? '') : ''
  if (additionalCookiesHeaderValue !== '') {
    config.headers['Additional-Cookies'] = additionalCookiesHeaderValue
  }

  if (CONFIGS.X_SOURCE_ORIGIN) {
    config.headers['X-Source-Origin'] = CONFIGS.X_SOURCE_ORIGIN
  }

  if (CONFIGS.BASE_URL) {
    config.baseURL = CONFIGS.BASE_URL + '/api'
  }

  // React Native: restore Bearer token from storage on every request
  // so authenticated requests work correctly after app restarts
  if (!isBrowser && !config.headers['Authorization']) {
    const accessToken = storage.getItem('access_token')
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
      publicRequest.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    }
  }

  return config
})

publicRequest.interceptors.response.use(
  (response: AxiosResponse) => {
    const authGuestToken: string | null = _get(
      response,
      'headers.authorization-guest',
      ''
    )

    if (authGuestToken) {
      storage.setItem('auth_guest_token', authGuestToken)
      publicRequest.setGuestToken(authGuestToken)
    }

    const xtfCookie = _get(response, 'headers.x-tf-ecommerce')
    const url = _get(response, 'config.url')
    const method = _get(response, 'config.method')

    if (xtfCookie && !(url === '/auth' && method === 'delete')) {
      setCustomCookie('X-TF-ECOMMERCE', xtfCookie)
    }

    return response
  },
  (error: AxiosError) => {
    if (error?.response?.status === 401) {
      storage.removeItem('user_data')
      if (isBrowser) {
        const errorType = _get(error, 'response.data.error')
        if (errorType === 'invalid_token') {
          window.location.href = '/'
        }
      } else {
        // React Native: clear expired Bearer token so the app can re-authenticate
        storage.removeItem('access_token')
        storage.removeItem('refresh_token')
        delete publicRequest.defaults.headers.common['Authorization']
      }
    }

    const authGuestToken = _get(error, 'response.headers.authorization-guest')
    if (authGuestToken) {
      storage.setItem('auth_guest_token', authGuestToken)
      publicRequest.setGuestToken(authGuestToken)
    }

    return Promise.reject(error)
  }
)
